/**
 * Public Privacy Policy — reachable without a session at /privacy.
 *
 * Dual-language: English first, then a Portuguese (Brazilian) translation
 * as a second section on the same page. A sticky language toggle lets the
 * reader jump between them without scrolling.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { LegalLayout } from "./LegalLayout";

type Lang = "en" | "pt";

const EFFECTIVE_DATE = "April 17, 2026";
const EFFECTIVE_DATE_PT = "17 de abril de 2026";

export function PrivacyPage() {
  const [lang, setLang] = useState<Lang>("en");

  return (
    <LegalLayout
      title={lang === "en" ? "Privacy Policy" : "Política de Privacidade"}
      effectiveDate={lang === "en" ? EFFECTIVE_DATE : EFFECTIVE_DATE_PT}
      sibling={{
        to: "/terms",
        label: lang === "en" ? "Read the Terms of Service →" : "Ler os Termos de Serviço →",
      }}
    >
      {/* Language toggle */}
      <div className="flex items-center gap-2 -mt-4 mb-8">
        <button
          type="button"
          onClick={() => setLang("en")}
          className={
            "px-3 py-1 rounded-full text-xs font-medium transition-colors " +
            (lang === "en"
              ? "bg-accent text-white"
              : "bg-surface border border-border text-text-muted hover:text-text-primary")
          }
        >
          English
        </button>
        <button
          type="button"
          onClick={() => setLang("pt")}
          className={
            "px-3 py-1 rounded-full text-xs font-medium transition-colors " +
            (lang === "pt"
              ? "bg-accent text-white"
              : "bg-surface border border-border text-text-muted hover:text-text-primary")
          }
        >
          Português (Brasil)
        </button>
      </div>

      {lang === "en" ? <PrivacyEN /> : <PrivacyPT />}
    </LegalLayout>
  );
}

// ── English ────────────────────────────────────────────────────────────

function PrivacyEN() {
  return (
    <>
      <p>
        Lenavio, Inc. (&ldquo;Lenavio,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) is a company based
        in the United States, with its principal place of business in the
        State of Florida. This Privacy Policy explains what personal data
        we handle when you or your employer uses the Lenavio HR platform
        (the &ldquo;Service&rdquo;), why we handle it, and the rights you have over
        that data.
      </p>
      <p>
        In most cases the company that subscribed to Lenavio on your
        behalf (your employer or client) is the <strong>data controller</strong>:
        they decide which data to upload and how to use it. Lenavio acts
        as their <strong>data processor</strong>, handling that data only on their
        documented instructions and to operate the Service. If you have a
        question about specific records (access, correction, deletion),
        the first step is usually to contact your employer&apos;s HR team.
      </p>

      <Section title="1. Information we collect">
        <p>
          We collect information that your employer provides when setting
          up and using the Service. Depending on the modules your employer
          enables, this may include:
        </p>
        <ul>
          <li>
            <strong>Identity and contact details</strong> — full name, work and
            personal email addresses, phone numbers, home address, date of
            birth, national ID or tax ID where legally required.
          </li>
          <li>
            <strong>Employment information</strong> — role, department, manager,
            start date, employment status, employment history, performance
            reviews, training records.
          </li>
          <li>
            <strong>Compensation data</strong> — salary, bonus, equity, payroll
            details, invoices for contractors.
          </li>
          <li>
            <strong>Time and attendance</strong> — time-off requests, approvals,
            balances, clock-in/clock-out records.
          </li>
          <li>
            <strong>Complaints and investigations</strong> — text, metadata, and
            attachments submitted through the complaints module, including
            anonymous submissions.
          </li>
          <li>
            <strong>Documents and media</strong> — HR documents uploaded by you or
            about you, profile photos, signed acknowledgements.
          </li>
          <li>
            <strong>Account and usage data</strong> — authentication records, IP
            addresses, browser type, pages visited within the Service,
            audit logs of actions taken.
          </li>
        </ul>
      </Section>

      <Section title="2. Why we process this data">
        <p>We process personal data to:</p>
        <ul>
          <li>Provide the HR services your employer has contracted us to deliver.</li>
          <li>Authenticate users and keep tenants isolated from one another.</li>
          <li>Generate reports, payroll exports, and compliance records that your employer needs to run their business.</li>
          <li>Provide AI-assisted features (question answering, drafting, pattern detection) strictly within your employer&apos;s tenant.</li>
          <li>Investigate security incidents and prevent abuse.</li>
          <li>Meet our legal, accounting, and regulatory obligations.</li>
        </ul>
      </Section>

      <Section title="3. Legal basis for processing">
        <p>
          Depending on the data subject&apos;s jurisdiction, we rely on one or
          more of the following legal bases:
        </p>
        <ul>
          <li>
            <strong>Contractual necessity</strong> — we process the data because
            your employer hired us to provide the Service, and the
            processing is necessary to perform that contract.
          </li>
          <li>
            <strong>Legitimate interest</strong> — for security, fraud prevention,
            service improvement, and similar operational needs, balanced
            against your rights and expectations.
          </li>
          <li>
            <strong>Legal obligation</strong> — where laws require us to retain
            records (for example, tax and employment records).
          </li>
          <li>
            <strong>Consent</strong> — where required by law (for example, certain
            voluntary disclosures or optional profile information), we
            process the data only with your explicit consent, which you
            may withdraw at any time.
          </li>
        </ul>
      </Section>

      <Section title="4. Who we share data with">
        <p>
          We do <strong>not</strong> sell your personal data. We do not share it
          with third parties for their own marketing. We share personal
          data only with the following categories of subprocessors, each
          under a written agreement that restricts them to processing on
          our documented instructions:
        </p>
        <ul>
          <li>
            <strong>Hosting and infrastructure</strong> — Render (application
            hosting), Neon (PostgreSQL database).
          </li>
          <li>
            <strong>Content delivery</strong> — Vercel (frontend static hosting
            and CDN).
          </li>
          <li>
            <strong>AI services</strong> — Anthropic, PBC (Claude), which powers
            the AI-assisted features. Data sent to Anthropic is covered by
            their enterprise data-protection terms and is not used to
            train their models.
          </li>
          <li>
            <strong>Payment processing</strong> — Stripe, Inc., for billing and
            subscription management.
          </li>
          <li>
            <strong>Email delivery</strong> — transactional email provider
            (vendor to be confirmed). We will publish the provider name
            here before any production email is sent.
          </li>
        </ul>
        <p>
          A current list of subprocessors is available on request by
          writing to{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>
          .
        </p>
      </Section>

      <Section title="5. International transfers">
        <p>
          Lenavio is based in the United States, and personal data is
          stored and processed there. Where required for data subjects in
          Brazil (under the LGPD) or the European Economic Area and United
          Kingdom (under the GDPR and UK GDPR), transfers are governed by
          the European Commission&apos;s Standard Contractual Clauses or an
          equivalent safeguard.
        </p>
      </Section>

      <Section title="6. Data retention">
        <p>
          We retain personal data for as long as the employer&apos;s
          subscription is active. When a subscription ends, we retain the
          data for <strong>30 days</strong> so the employer can export anything they
          need, and then we permanently delete it from our production
          systems. Encrypted backups are rotated out on their normal
          schedule (at most 35 days). Some records may be kept longer
          where the law requires it (for example, tax or payroll
          records).
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          Depending on where you live, you may have the following rights
          over your personal data. These rights exist under the Brazilian
          LGPD, the EU/UK GDPR, and the California CCPA, among others:
        </p>
        <ul>
          <li><strong>Access</strong> — confirm whether we process your data and receive a copy.</li>
          <li><strong>Correction</strong> — have inaccurate data corrected.</li>
          <li><strong>Deletion</strong> — have your data deleted, subject to legal retention requirements.</li>
          <li><strong>Portability</strong> — receive your data in a structured, machine-readable format.</li>
          <li><strong>Objection or restriction</strong> — object to, or ask us to restrict, certain processing.</li>
          <li><strong>Withdrawal of consent</strong> — where we rely on consent, you can withdraw it at any time.</li>
          <li><strong>Non-discrimination</strong> — we will not retaliate against you for exercising any of these rights.</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>
          . Because we are a processor, we will usually forward your
          request to your employer (the controller) and coordinate a
          response with them.
        </p>
      </Section>

      <Section title="8. Cookies and tracking">
        <p>
          We use <strong>essential cookies only</strong>, limited to session
          authentication and security. We do not use advertising cookies,
          cross-site tracking, or third-party analytics cookies. Your
          preferences (language, theme) are stored locally in your browser.
        </p>
      </Section>

      <Section title="9. Children">
        <p>
          The Service is intended for workplace use by adults. We do not
          knowingly collect personal data from anyone under the age of 16.
          If you believe a minor&apos;s data has been uploaded, contact us at{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>{" "}
          and we will delete it.
        </p>
      </Section>

      <Section title="10. Security">
        <p>We protect personal data with:</p>
        <ul>
          <li>Encryption in transit (TLS 1.2+) and at rest.</li>
          <li>Role-based access control and least-privilege permissions.</li>
          <li>Strict tenant isolation — each employer&apos;s data is scoped to its own organization and enforced at every query.</li>
          <li>Append-only audit logs for sensitive actions.</li>
          <li>Regular security reviews and dependency scanning.</li>
        </ul>
        <p>
          No system is perfectly secure, but we take reasonable and
          appropriate measures, and we will notify affected parties
          promptly if a breach of personal data occurs, consistent with
          our legal obligations.
        </p>
      </Section>

      <Section title="11. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. If we make
          <strong> material changes</strong>, we will notify the administrators of
          each tenant by email at least <strong>30 days</strong> before the changes
          take effect. Non-material changes (typos, formatting) may be
          made at any time.
        </p>
      </Section>

      <Section title="12. Contact us">
        <p>
          Questions about this policy, or requests to exercise your
          rights, go to{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>
          . Written correspondence can be addressed to Lenavio, Inc.,
          Florida, United States.
        </p>
      </Section>
    </>
  );
}

// ── Português (Brasil) ────────────────────────────────────────────────

function PrivacyPT() {
  return (
    <>
      <p>
        A Lenavio, Inc. (&ldquo;Lenavio&rdquo;, &ldquo;nós&rdquo;) é uma empresa sediada nos
        Estados Unidos, com sede no estado da Flórida. Esta Política de
        Privacidade explica quais dados pessoais tratamos quando você ou o
        seu empregador utiliza a plataforma de RH Lenavio (o
        &ldquo;Serviço&rdquo;), por que os tratamos e quais são os seus direitos
        sobre esses dados.
      </p>
      <p>
        Na maioria dos casos, a empresa que contratou a Lenavio em seu
        nome (seu empregador ou cliente) é o <strong>controlador</strong> dos
        dados: é ela quem decide quais informações carregar e como
        utilizá-las. A Lenavio atua como <strong>operador</strong> desses dados,
        tratando-os apenas conforme as instruções documentadas do
        controlador e para operar o Serviço. Se você tiver uma dúvida
        sobre registros específicos (acesso, correção, exclusão), o
        caminho inicial é geralmente contatar o RH do seu empregador.
      </p>

      <Section title="1. Dados que coletamos">
        <p>
          Coletamos dados que o seu empregador fornece ao configurar e
          utilizar o Serviço. Dependendo dos módulos habilitados, isso
          pode incluir:
        </p>
        <ul>
          <li>
            <strong>Identificação e contato</strong> — nome completo, e-mails
            profissional e pessoal, telefones, endereço residencial, data
            de nascimento, CPF ou outro documento fiscal quando exigido
            por lei.
          </li>
          <li>
            <strong>Informações funcionais</strong> — cargo, departamento, gestor,
            data de admissão, situação do vínculo, histórico profissional,
            avaliações de desempenho, registros de treinamento.
          </li>
          <li>
            <strong>Dados de remuneração</strong> — salário, bônus, participação,
            dados de folha e notas fiscais de prestadores.
          </li>
          <li>
            <strong>Jornada e ausências</strong> — solicitações de férias/folgas,
            aprovações, saldos, registros de ponto.
          </li>
          <li>
            <strong>Denúncias e apurações</strong> — textos, metadados e anexos
            enviados pelo canal de denúncias, incluindo denúncias
            anônimas.
          </li>
          <li>
            <strong>Documentos e mídia</strong> — documentos de RH enviados por
            você ou a seu respeito, fotos de perfil, assinaturas e
            declarações.
          </li>
          <li>
            <strong>Dados de conta e uso</strong> — registros de autenticação,
            endereços IP, tipo de navegador, páginas visitadas no Serviço,
            trilhas de auditoria.
          </li>
        </ul>
      </Section>

      <Section title="2. Por que tratamos esses dados">
        <p>Tratamos dados pessoais para:</p>
        <ul>
          <li>Fornecer os serviços de RH contratados pelo seu empregador.</li>
          <li>Autenticar usuários e manter os dados de cada cliente isolados.</li>
          <li>Gerar relatórios, exportações de folha e registros de conformidade exigidos pelo empregador.</li>
          <li>Oferecer recursos assistidos por IA (respostas, redação, detecção de padrões) estritamente dentro do ambiente do empregador.</li>
          <li>Investigar incidentes de segurança e coibir uso indevido.</li>
          <li>Cumprir obrigações legais, contábeis e regulatórias.</li>
        </ul>
      </Section>

      <Section title="3. Bases legais">
        <p>
          Dependendo da jurisdição do titular, utilizamos uma ou mais das
          seguintes bases legais:
        </p>
        <ul>
          <li>
            <strong>Execução de contrato</strong> — o tratamento é necessário para
            cumprir o contrato firmado com o empregador e entregar o
            Serviço.
          </li>
          <li>
            <strong>Legítimo interesse</strong> — para segurança, prevenção a
            fraudes, melhoria do Serviço e necessidades operacionais
            similares, sempre ponderando seus direitos e expectativas.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal ou regulatória</strong> — para
            guarda de registros exigidos por lei (por exemplo, trabalhista
            e fiscal).
          </li>
          <li>
            <strong>Consentimento</strong> — quando a lei exigir (por exemplo,
            divulgações voluntárias ou campos opcionais de perfil),
            tratamos os dados somente mediante consentimento explícito,
            que pode ser retirado a qualquer momento.
          </li>
        </ul>
      </Section>

      <Section title="4. Com quem compartilhamos">
        <p>
          <strong>Não vendemos</strong> seus dados pessoais e não os compartilhamos
          com terceiros para marketing próprio. Compartilhamos dados
          apenas com os operadores abaixo, sempre por contrato que os
          limita a tratar os dados conforme nossas instruções:
        </p>
        <ul>
          <li>
            <strong>Hospedagem e infraestrutura</strong> — Render (hospedagem da
            aplicação), Neon (banco de dados PostgreSQL).
          </li>
          <li>
            <strong>CDN / front-end</strong> — Vercel (hospedagem e distribuição
            do front-end).
          </li>
          <li>
            <strong>Serviços de IA</strong> — Anthropic, PBC (Claude), que
            viabilizam os recursos assistidos por IA. Os dados enviados à
            Anthropic são regidos pelos termos empresariais de proteção de
            dados da provedora e <strong>não são utilizados para treinar
            modelos</strong>.
          </li>
          <li>
            <strong>Pagamentos</strong> — Stripe, Inc., para faturamento e gestão
            de assinaturas.
          </li>
          <li>
            <strong>Entrega de e-mails</strong> — provedor transacional (a
            confirmar). Publicaremos aqui o nome do provedor antes do
            envio de e-mails em produção.
          </li>
        </ul>
        <p>
          A lista atualizada de operadores pode ser solicitada por
          e-mail:{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>
          .
        </p>
      </Section>

      <Section title="5. Transferências internacionais">
        <p>
          A Lenavio está sediada nos Estados Unidos, e os dados pessoais
          são armazenados e tratados lá. Para titulares no Brasil (LGPD)
          ou no Espaço Econômico Europeu e no Reino Unido (GDPR e UK
          GDPR), as transferências são realizadas com base nas Cláusulas
          Contratuais Padrão da Comissão Europeia ou em salvaguarda
          equivalente.
        </p>
      </Section>

      <Section title="6. Retenção">
        <p>
          Mantemos os dados pessoais enquanto a assinatura do empregador
          estiver ativa. Ao término da assinatura, retemos os dados por{" "}
          <strong>30 dias</strong> para que o empregador possa exportar o que
          precisar, e então os excluímos em definitivo dos sistemas de
          produção. Backups criptografados seguem seu ciclo normal de
          rotação (no máximo 35 dias). Alguns registros podem ser
          mantidos por prazo maior quando exigido por lei (por exemplo,
          registros fiscais e trabalhistas).
        </p>
      </Section>

      <Section title="7. Seus direitos">
        <p>
          A depender da sua localidade, você pode exercer os seguintes
          direitos, previstos na LGPD, GDPR, CCPA e legislações similares:
        </p>
        <ul>
          <li><strong>Acesso</strong> — confirmar o tratamento e obter cópia dos dados.</li>
          <li><strong>Correção</strong> — corrigir dados incompletos, inexatos ou desatualizados.</li>
          <li><strong>Eliminação</strong> — solicitar a exclusão, respeitados os prazos de retenção legal.</li>
          <li><strong>Portabilidade</strong> — receber os dados em formato estruturado e interoperável.</li>
          <li><strong>Oposição ou limitação</strong> — opor-se ou restringir determinados tratamentos.</li>
          <li><strong>Revogação do consentimento</strong> — quando a base for o consentimento, você pode revogá-lo a qualquer momento.</li>
          <li><strong>Não discriminação</strong> — o exercício desses direitos não implicará qualquer retaliação.</li>
        </ul>
        <p>
          Para exercer qualquer desses direitos, envie um e-mail para{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>
          . Como somos operadores, normalmente encaminharemos a
          solicitação ao seu empregador (controlador) e coordenaremos a
          resposta.
        </p>
      </Section>

      <Section title="8. Cookies e rastreamento">
        <p>
          Utilizamos <strong>apenas cookies essenciais</strong>, limitados à
          autenticação de sessão e à segurança. Não utilizamos cookies de
          publicidade, rastreamento entre sites ou cookies de análise de
          terceiros. Suas preferências (idioma, tema) ficam armazenadas
          localmente no seu navegador.
        </p>
      </Section>

      <Section title="9. Crianças e adolescentes">
        <p>
          O Serviço é destinado a uso corporativo por pessoas adultas. Não
          coletamos conscientemente dados pessoais de menores de 16 anos.
          Se você acredita que dados de um menor foram carregados,
          contate-nos em{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>{" "}
          e providenciaremos a exclusão.
        </p>
      </Section>

      <Section title="10. Segurança">
        <p>Protegemos os dados pessoais com:</p>
        <ul>
          <li>Criptografia em trânsito (TLS 1.2+) e em repouso.</li>
          <li>Controle de acesso baseado em papéis e princípio do menor privilégio.</li>
          <li>Isolamento estrito entre clientes — os dados de cada empregador ficam no escopo de sua organização e essa regra é reforçada em toda consulta.</li>
          <li>Trilhas de auditoria imutáveis para ações sensíveis.</li>
          <li>Revisões periódicas de segurança e varredura de dependências.</li>
        </ul>
        <p>
          Nenhum sistema é perfeitamente seguro, mas adotamos medidas
          razoáveis e adequadas e comunicaremos prontamente os afetados
          em caso de incidente, conforme as obrigações legais aplicáveis.
        </p>
      </Section>

      <Section title="11. Alterações desta política">
        <p>
          Podemos atualizar esta Política de Privacidade periodicamente.
          Quando houver <strong>alterações materiais</strong>, notificaremos os
          administradores de cada cliente por e-mail com pelo menos{" "}
          <strong>30 dias</strong> de antecedência. Alterações não materiais (como
          correções de redação e formatação) podem ser feitas a qualquer
          momento.
        </p>
      </Section>

      <Section title="12. Contato">
        <p>
          Dúvidas sobre esta política ou pedidos de exercício de direitos
          devem ser enviados para{" "}
          <a className="text-accent hover:underline" href="mailto:privacy@lenavio.com">
            privacy@lenavio.com
          </a>
          . Correspondências podem ser endereçadas à Lenavio, Inc.,
          Flórida, Estados Unidos.
        </p>
      </Section>
    </>
  );
}

// ── Small layout helpers ──────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold font-heading text-text-primary pt-4">
        {title}
      </h2>
      <div className="space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_a]:break-words">
        {children}
      </div>
    </section>
  );
}
